import { ActionCentre } from "./ActionCentre";
import { OfferPopup } from "./OfferPopup";
import { IPopupSettings, ISliderOptions } from "./types";

declare var _gaq: any;

export class CardsSlider {
    private actionService: ActionCentre;
    private options: ISliderOptions;
    private cardCount: number = 0;
    private cardHtml: string[] = [];
    private cardWidth: number;
    private cardMatrix: number[];
    private cardClass: string;
    private animating: boolean;
    private resizeTimeout: any;
    private xDown: any = null;
    private yDown: any = null;
    private originalVisibleCards: number;

    constructor(options: ISliderOptions, actionsService: ActionCentre) {
        this.options = options;
        this.actionService = actionsService;
        this.originalVisibleCards = this.options.visibleCards;

        const stage: HTMLElement = document.querySelector(`[${this.options.stageSelector}]`) as HTMLElement;

        if (stage !== null) {
            this.Resize();

            window.addEventListener("resize", this.Resize.bind(this), false);
            document.addEventListener("touchstart", this.HandleTouchStart.bind(this), false);
            document.addEventListener("touchmove", this.HandleTouchMove.bind(this), false);
        }
    }

    private BasicSetup(): void {
        const cardsSelector: string = `[${this.options.stageSelector}] [${this.options.cardSelector}]`;
        const cards: NodeListOf<HTMLElement> = document.querySelectorAll(cardsSelector) as NodeListOf<HTMLElement>;
        const stage: HTMLElement = document.querySelector(`[${this.options.stageSelector}]`) as HTMLElement;

        stage.style.width = "auto";

        this.cardWidth = stage.clientWidth / this.options.visibleCards; 

        if (cards.length > 0) {
            this.cardClass = cards[0].classList[0];
        }
        
        if (this.cardHtml.length === 0) {
            this.cardCount = cards.length;

            for (let i = 0; i < this.cardCount; i++) {
                this.cardHtml.push(cards[i].innerHTML);
            }
        }
        
        this.cardMatrix = this.CreateStartingMatrix();
        this.PrepareStage();
        this.PopulateStageByMatrix();

        this.AddDots();
        this.CheckDotsStatus();
    }

    private BindEvents(): void {
        const dotsSelector: string = `[${this.options.paginationSelector}] [${this.options.dotSelector}]`;
        const prev: HTMLElement = document.querySelector(`[${this.options.prevSelector}]`) as HTMLElement;
        const next: HTMLElement = document.querySelector(`[${this.options.nextSelector}]`) as HTMLElement;
        const dots: NodeListOf<HTMLElement> = document.querySelectorAll(dotsSelector) as NodeListOf<HTMLElement>;

        prev.addEventListener("click", () => {
            this.Move(-1);
            _gaq.push(["_trackEvent", "Interaction", `Move Card Slider (${this.options.nameForGoogleAnalytics})`, window.location.pathname, 0, false]);
        });   // -1 just means backwards.
        next.addEventListener("click", () => {
            this.Move(1);
            _gaq.push(["_trackEvent", "Interaction", `Move Card Slider (${this.options.nameForGoogleAnalytics})`, window.location.pathname, 0, false]);
        });    // positive number just means forward.

        for (let i = 1; i <= dots.length; i++) {
            dots[i - 1].addEventListener("click", () => {
                _gaq.push(["_trackEvent", "Interaction", `Move Card Slider (${this.options.nameForGoogleAnalytics})`, window.location.pathname, 0, false]);
                this.MoveDots(i);
            });
        }
    }

    private HandleTouchStart(evt): void {
        const coordVer: number[] = [];
        const stage: HTMLElement = document.querySelector(`[${this.options.outerStageSelector}]`) as HTMLElement;
        const stageRect: ClientRect = stage.getBoundingClientRect();

        coordVer[0] = stageRect.top + window.pageYOffset;
        coordVer[1] = coordVer[0] + stage.clientHeight;

        if (evt.touches[0].pageY > coordVer[0] && evt.touches[0].pageY < coordVer[1]) {
            this.xDown = evt.touches[0].clientX;
            this.yDown = evt.touches[0].clientY;
        }
    }

    private HandleTouchMove(evt): void {
        if (!this.xDown || !this.yDown) {
            return;
        }

        const xUp = evt.touches[0].clientX;
        const yUp = evt.touches[0].clientY;

        const xDiff = this.xDown - xUp;
        const yDiff = this.yDown - yUp;

        if (Math.abs(xDiff) > Math.abs(yDiff)) {
            if (xDiff > 0) {
                // left swipe
                this.Move(1);
            } else {
                // right swipe
                this.Move(-1);
            }
        }

        this.xDown = null;
        this.yDown = null;
    }

    private Resize(): void {
        const windowWidth: number = window.innerWidth;

        if (windowWidth > 976) {
            this.options.visibleCards = this.originalVisibleCards;
        } else if (windowWidth <= 976 && windowWidth > 743) {
            this.options.visibleCards = 4;
        } else if (windowWidth <= 743 && windowWidth > 535) {
            this.options.visibleCards = 3;
        } else {
            this.options.visibleCards = 1;
        }

        this.BasicSetup();
        this.BindEvents();
    }

    private CreateStartingMatrix(): number[] {
        const arr: number[] = [];
        let last = this.cardCount + 1;
        for (let i = 1; i <= this.options.visibleCards * 2; i++) {
            arr.push(i);
        }
        for (let i = 0; i < this.options.visibleCards; i++) {
            last -= 1;
            if (last < 1) {
                last = this.cardCount;
            }
            arr.unshift(last);
        }
        return arr;
    }

    private PopulateStageByMatrix(): void {
        const stage: HTMLElement = document.querySelector(`[${this.options.stageSelector}]`) as HTMLElement;
        
        for (let i = 0; i < this.cardMatrix.length; i++) {
            const html: HTMLElement = document.createElement("div");
            html.setAttribute(this.options.cardSelector, "");
            html.classList.add(this.cardClass);
            html.style.width = `${this.cardWidth}px`;
            if (this.cardHtml[this.cardMatrix[i] - 1] !== undefined) {
                html.innerHTML = this.cardHtml[this.cardMatrix[i] - 1];
            }
            stage.appendChild(html);
        }

        this.CheckDotsStatus();

        if (this.options.popups) {
            const popupSettings: IPopupSettings = {
                offerStageSelector: this.options.stageSelector,
                offerUniqueSelector: "data-offer-id",
                sliderId: this.options.sliderId,
            };
            const popups: OfferPopup = new OfferPopup(this.actionService, popupSettings);
        }

        if (this.options.wistia) {
            const videoPopUpSelector: string = ".where-when__video a";
            const videoPopUp: HTMLElement = document.querySelector(videoPopUpSelector) as HTMLElement;           
            const videoPopUpTrigger: HTMLElement = document.querySelector(".offer--video") as HTMLElement;

            if (videoPopUpTrigger) {
                stage.addEventListener("click", (e) => {
                    let doit: boolean = false;
                    const target: HTMLElement = e.target as HTMLElement;
                    for (let i = 0; i < target.classList.length; i++) {
                        if (target.classList[i].indexOf("offer--video") > -1) {
                            doit = true;
                        }
                    } 
                    if (doit) {
                        videoPopUp.click();
                        _gaq.push(["_trackEvent", "Interaction", `View Video (${this.options.nameForGoogleAnalytics})`, window.location.pathname, 0, false]);
                    }                    
                });
            }
        }
    }

    private PrepareStage(): void {
        const stage: HTMLElement = document.querySelector(`[${this.options.stageSelector}]`) as HTMLElement;
        stage.innerHTML = "";
        stage.style.width = `${Math.round(this.cardWidth) * (this.options.visibleCards * 3)}px`;
        stage.style.left = `-${this.cardWidth * this.options.visibleCards}px`;
    }

    private Move(direction: number): void {
        if (!this.animating) {
            const stage: HTMLElement = document.querySelector(`[${this.options.stageSelector}]`) as HTMLElement;
            stage.classList.add("animating");

            stage.style.left = direction > 0 ? `-${this.cardWidth * 2 * this.options.visibleCards}px` : "0px"; 
            stage.style.opacity = "0.1";

            this.animating = true;
            setTimeout(() => {
                stage.style.opacity = "1";
                stage.classList.remove("animating");
                this.PrepareCardMatrixForDirection(direction);
                this.PrepareStage();
                this.PopulateStageByMatrix();
                this.animating = false;
            }, 200);
        }
    }

    private AddDots(): void {
        const controls: HTMLElement = document.querySelector(`[data-offer-stage-controls="${this.options.sliderId}"]`) as HTMLElement;
        const paginationNameSelector: string = `[data-offer-pagination-name="${this.options.sliderId}"]`;
        const paginationName: HTMLElement = document.querySelector(paginationNameSelector) as HTMLElement;
        const pagination: HTMLElement = document.querySelector(`[${this.options.paginationSelector}]`) as HTMLElement;
        const dotCount: number = Math.ceil(this.cardCount / this.options.visibleCards);

        pagination.innerHTML = "";

        for (let i = 0; i < dotCount; i++) {
            const div = document.createElement("div");
            div.setAttribute(this.options.dotSelector, `${i + 1}`);
            div.classList.add("dot");

            pagination.appendChild(div);
        }

        if (pagination.children.length === 1) {
            controls.classList.add("hide");
            pagination.classList.add("hide");
            paginationName.classList.add("hide");
        } else {
            controls.classList.remove("hide");
            pagination.classList.remove("hide");
            paginationName.classList.remove("hide");
        }
    }

    private MoveDots(n: number): void {
        if (!this.animating) {
            const stage: HTMLElement = document.querySelector(`[${this.options.stageSelector}]`) as HTMLElement;

            stage.classList.add("animating");
            stage.style.opacity = "0.1";

            this.MakeDotActive(n);
            this.animating = true;
            setTimeout(() => {
                this.PrepareCardMatrixForN(n);
                this.PrepareStage();
                this.PopulateStageByMatrix();
                this.animating = false;
                stage.classList.remove("animating");
                stage.style.opacity = "1";
            }, 200);
        }
    }

    private PrepareCardMatrixForDirection(direction: number): void {
        const dotsSelector: string = `[${this.options.paginationSelector}] [${this.options.dotSelector}]`;
        const dots: NodeListOf<HTMLElement> = document.querySelectorAll(dotsSelector) as NodeListOf<HTMLElement>;
        const dotCount: number = dots.length;
        const n: number = this.cardMatrix[this.options.visibleCards];
  
        if (n === 1) {
            if (direction > 0) {
                this.PrepareCardMatrixForN(2);
            } else {
                this.PrepareCardMatrixForN(dotCount);
            }
        } else {
            const dotNumber: number = Math.floor((n - 1) / this.options.visibleCards);           
            if (direction > 0) {
                if (dotNumber + 2 > dotCount) {
                    this.PrepareCardMatrixForN(1);
                } else {
                    this.PrepareCardMatrixForN(dotNumber + 2);
                }
            } else {
                this.PrepareCardMatrixForN(dotNumber);
            }
        }    
    }

    private PrepareCardMatrixForN(n: number): void {
        const startingNumber: number = (n - 1) * this.options.visibleCards + 1;
        this.cardMatrix[this.options.visibleCards] = startingNumber;

        let prev: number;
        let current: number;
        let next: number;

        for (let i = 0; i < this.options.visibleCards; i++) {
            prev = startingNumber - (this.options.visibleCards - i);
            if (prev < 1) { prev = this.cardCount - this.options.visibleCards + i + 1; }
            this.cardMatrix[i] = prev;

            if (current < this.options.visibleCards) {
                current += 1;
            } else {
                current = startingNumber + i;
            }

            if (current > this.cardCount) { current = 1; }
            this.cardMatrix[i + this.options.visibleCards] = current;

            next = startingNumber + this.options.visibleCards + i;
            if (next > this.cardCount) { next = 1 + i; }
            this.cardMatrix[i + this.options.visibleCards * 2] = next;
        }
    }

    private CheckDotsStatus(): void {
        const num = this.cardMatrix[this.options.visibleCards];
        if (num === 1) {
            this.MakeDotActive(1);
        } else if ((num - 1) % this.options.visibleCards === 0) {
            this.MakeDotActive((num - 1) / this.options.visibleCards + 1);
        } else {
            this.MakeDotActive(100);
        }
    }

    private MakeDotActive(n: number): void {
        const dotsSelector: string = `[${this.options.paginationSelector}] [${this.options.dotSelector}]`;
        const dots: NodeListOf<HTMLElement> = document.querySelectorAll(dotsSelector) as NodeListOf<HTMLElement>;

        for (let i = 0; i < dots.length; i++) {
            if (i + 1 === n) {
                dots[i].classList.add("active");
            } else {
                dots[i].classList.remove("active");
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/global/CardsSlider.ts